import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink, updatePassword } from 'firebase/auth';
import { auth } from '../firebase';
import '../styles/App.css';

type Step = 'loading' | 'email-prompt' | 'set-password' | 'error';

export default function SetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('loading');
  const [emailInput, setEmailInput] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      navigate('/home', { replace: true });
      return;
    }
    // Try to get email from URL param (admin encodes it when sending)
    const params = new URLSearchParams(window.location.search);
    const emailFromUrl = params.get('email');
    if (emailFromUrl) {
      completeSignIn(emailFromUrl);
    } else {
      setStep('email-prompt');
    }
  }, []); // eslint-disable-line

  async function completeSignIn(email: string) {
    setBusy(true);
    try {
      await signInWithEmailLink(auth, email, window.location.href);
      localStorage.removeItem('emailForSignIn');
      window.history.replaceState(null, '', '/setup');
      setUserEmail(email);
      setStep('set-password');
    } catch {
      setError('This link is invalid or has already been used. Please contact support to get a new one.');
      setStep('error');
    } finally {
      setBusy(false);
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed) return;
    completeSignIn(trimmed);
  }

  async function handleSetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Minimum 8 characters'); return; }
    setBusy(true);
    try {
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, password);
      }
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Failed to set password. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (step === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f1ea' }}>
        <div style={{ fontFamily: 'DM Sans, sans-serif', color: '#8a8680', fontSize: '14px' }}>Setting up your account…</div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f1ea', padding: '24px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px 32px', maxWidth: '420px', width: '100%', textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <h2 style={{ fontFamily: 'Libre Baskerville, serif', fontSize: '20px', fontWeight: 700, color: '#1a2e1f', marginBottom: '10px' }}>Link expired or already used</h2>
          <p style={{ fontSize: '14px', color: '#6b7c6e', lineHeight: 1.6, marginBottom: '24px' }}>{error}</p>
          <a href="mailto:support@marketentrypro.com" style={{ color: '#1a6b3c', fontWeight: 600, textDecoration: 'none', fontSize: '14px' }}>Contact Support →</a>
        </div>
      </div>
    );
  }

  return (
    <div className="login-overlay" style={{ background: 'rgba(26,46,31,0.85)' }}>
      <div className="login-dialog" onClick={e => e.stopPropagation()}>

        {/* Left panel */}
        <div className="login-left">
          <div className="login-left-inner">
            <div className="login-tag"><span className="login-tag-dot"></span><span className="login-tag-line"></span>Account Setup</div>
            <h1 className="login-heading">
              {step === 'email-prompt'
                ? <>Confirm your<br /><em>identity.</em></>
                : <>Set your<br /><em>password.</em></>
              }
            </h1>
            <p className="login-desc">
              {step === 'email-prompt'
                ? 'Enter the email address where you received your account setup link.'
                : 'Your account has been approved. Create a password to complete setup and access your dashboard.'
              }
            </p>
          </div>
          <div>
            <div className="login-quote">
              <div className="login-quote-bar"></div>
              <div>
                <div className="login-quote-text">"Your simulation is ready. Set up your account to access your full market entry analysis."</div>
                <div className="login-quote-by">Market Entry Pro · Onboarding</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className="login-right">
          <div className="login-top-bar"></div>
          <div className="login-right-inner">

            {step === 'email-prompt' && (
              <>
                <div className="login-eyebrow">Confirm Identity</div>
                <div className="login-title">Verify your email</div>
                <div className="login-sub">Enter the email address where you received the account setup link.</div>
                {error && (
                  <div className="login-alert error">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span>{error}</span>
                  </div>
                )}
                <form onSubmit={handleEmailSubmit}>
                  <div className="login-group">
                    <label>Email address</label>
                    <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="you@company.com" autoFocus required />
                  </div>
                  <button type="submit" className={`login-submit ${busy ? 'loading' : ''}`} disabled={busy}>
                    {busy
                      ? <div className="login-spinner"></div>
                      : <><span>Continue</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></>
                    }
                  </button>
                </form>
              </>
            )}

            {step === 'set-password' && (
              <>
                <div className="login-eyebrow">Final Step</div>
                <div className="login-title">Create your password</div>
                <div className="login-sub">
                  Setting up account for <strong style={{ color: '#1a2e1f' }}>{userEmail}</strong>
                </div>
                {error && (
                  <div className="login-alert error">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span>{error}</span>
                  </div>
                )}
                <form onSubmit={handleSetPassword}>
                  <div className="login-group">
                    <label>Password</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters" autoFocus required />
                  </div>
                  <div className="login-group">
                    <label>Confirm password</label>
                    <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password" required />
                  </div>
                  <button type="submit" className={`login-submit ${busy ? 'loading' : ''}`} disabled={busy}>
                    {busy
                      ? <div className="login-spinner"></div>
                      : <><span>Complete setup &amp; go to dashboard</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></>
                    }
                  </button>
                </form>
                <div className="login-foot">
                  <div className="login-secure">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    Password is stored securely<span className="login-secure-dot"></span>256-bit SSL
                  </div>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
